import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { distinctUntilChanged, filter, map, shareReplay, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subject } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { OrderformPaymentProvider, OrderFormValidationResult } from '../../../index';
import { ANIMATION_slideInOut } from '../../animations';
import { ToasterService } from '@digistore/ds24-ui/toaster';
import { getInvalidFieldListFromFormGroup } from '../../misc/get-invalid-field-list-from-form-group';
import { isPageBuilderDesignMode } from '../../misc/pagebuilder';
import { SiteOwners } from '../../ngrx/orderform.models';
import { GrowthBookService } from '../../services/growthbook.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { PrimerPaymentMethodType } from './method/primer/primer.model';

@Component({
  selector: 'orderform-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrls: ['./payment-methods.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ANIMATION_slideInOut],
})
export class PaymentMethodsComponent implements OnInit, OnDestroy {
  private _textColor;
  private _placeholderColor;
  private _inputFieldColor;
  SiteOwners = SiteOwners;
  protected readonly isPageBuilderDesignMode = isPageBuilderDesignMode;
  protected readonly isPlainModeEnabled = toSignal(
    this._orderformFascade.globalSettings$.pipe(map((settings) => settings.is_plain_mode_enabled)),
    { initialValue: false }
  );

  private sanitizeCssColorInput(colorOrVariable: string) {
    // Find occurences of CSS Variables: "var( ... )"
    const cssVarRegex = /var\((.+)\)/g;
    const resolveColor = (cssVariableName) => {
      return getComputedStyle(document.documentElement).getPropertyValue(cssVariableName);
    };

    // check if input value is a css variable
    const result = cssVarRegex.exec(colorOrVariable);
    return result?.[1] ? resolveColor(result[1]) : colorOrVariable;
  }

  @Input()
  public set textColor(colorOrVariable: string) {
    this._textColor = this.sanitizeCssColorInput(colorOrVariable);
  }

  public get textColor() {
    return this._textColor;
  }

  @Input()
  public set placeholderColor(colorOrVariable: string) {
    this._placeholderColor = this.sanitizeCssColorInput(colorOrVariable);
    this._element.nativeElement.style.setProperty('--placeholder-color', this._placeholderColor);
  }

  public get placeholderColor() {
    return this._placeholderColor;
  }

  @Input()
  public set inputFieldColor(colorOrVariable: string) {
    this._inputFieldColor = this.sanitizeCssColorInput(colorOrVariable);
  }

  public get inputFieldColor() {
    return this._inputFieldColor;
  }

  @Input() mode: 'vertical' | 'horizontal' = 'vertical';
  @Input() showCostsHint: 'true' | 'false' = 'true';

  @Input() fontSize;
  @Input() paymentIconsTheme: 'light' | 'dark' = 'dark';

  private _destroy$ = new Subject<void>();
  private _blockSendUpdate = false;

  private _paymentProviderNotAvailable$ = new BehaviorSubject<string[]>([]);

  private _paymentMethodList$ = combineLatest([this._orderformFascade.order$, this._paymentProviderNotAvailable$]).pipe(
    map(([orderformData, notAvaiable]) => {
      return orderformData.payment.filter((provider) => notAvaiable.indexOf(provider.payment_provider_id) < 0);
    }),
    shareReplay(1)
  );

  protected readonly isExpressChekoutAvailable: Signal<boolean> = toSignal(
    this._orderformFascade.order$.pipe(
      map((orderformData) => !!orderformData?.express_checkout),
      distinctUntilChanged()
    ),
    { initialValue: false }
  );

  expressCheckoutConfig$ = this._orderformFascade.expressCheckout$;

  public detectedGoogleAppleMethodType: PrimerPaymentMethodType;

  /**
   * Contains only payment methods that can be selected.
   * e.g. void or alias are pre-selected by the backend and cannot
   * be changed anymore. In this case, the existing ones are
   * only shown as placeholders and disabled.
   */
  paymentMethodSelectionList$: Observable<OrderformPaymentProvider[]> = this._paymentMethodList$.pipe(
    map((paymentMethodList) =>
      paymentMethodList.filter((method) => method.pay_method !== 'void' && method.pay_method !== 'alias')
    ),
    map((availablePayments: OrderformPaymentProvider[]) => {
      const sizeFactor = 1.3;
      return availablePayments.map((payment) => {
        return {
          ...payment,
          icon_data: {
            ...payment.icon_data,
            height: payment.icon_data.height * sizeFactor,
            width: payment.icon_data.width * sizeFactor,
            large: payment.icon_data.large
              ? {
                  ...payment.icon_data.large,
                  height: payment.icon_data.large.height * sizeFactor,
                  width: payment.icon_data.large.width * sizeFactor,
                }
              : null,
            small: payment.icon_data.small
              ? {
                  ...payment.icon_data.small,
                  height: payment.icon_data.small.height * sizeFactor,
                  width: payment.icon_data.small.width * sizeFactor,
                }
              : null,
          },
        };
      });
    })
  );

  selectedPaymentMethod$ = this._orderformFascade.order$.pipe(
    map((e) => {
      return e.order.payment_provider;
    }),
    shareReplay(1)
  );

  formGroup = new UntypedFormGroup({
    providerId: new UntypedFormControl(),
    providerData: new UntypedFormControl({ valid: true }),
  });

  /**
   * There are 2 breakpoints:
   * 210 pixels needed for an option at minimum in the large variant
   * 110 pixels needed at minimum for the normal variant
   * bp-normal: Occurs when there is not enough space left for the large variant
   * bp-small: When there is not enough space left for the normal variant
   * (in css this is then used as rule .mode-small.normal)
   */
  breakpointConfiguration$ = this.paymentMethodSelectionList$.pipe(
    map((list) => {
      return {
        [list.length * 210]: 'bp-normal',
        [list.length * 110]: 'bp-small',
      };
    })
  );

  isAccessibilityToggleEnabled = computed(() => {
    return this.growthBookService.isFeatureEnabled('accessibility-toggle');
  });

  private _subscriptionBeforeSubmit = this._orderformFascade.formValidation.subscribe(
    () => this._onBeforeSubmit(),
    this._element
  );

  private _formValidationSubscription = this.formGroup.statusChanges
    .pipe(
      withLatestFrom(this.selectedPaymentMethod$),
      map(([, selectedMethod]) => {
        return (
          !(selectedMethod.disabled && selectedMethod.disabled_message) &&
          selectedMethod.status === null &&
          this.formGroup.valid
        );
      })
    )
    .subscribe((valid) => this.validate.emit({ valid, source: 'change' }));

  @Output() readonly validate = new EventEmitter<{
    valid: boolean;
    source: 'change' | 'submit';
  }>();

  private _submitFailedSubscription = this._orderformFascade.submitFailed
    .pipe(withLatestFrom(this.selectedPaymentMethod$))
    .subscribe(([, selectedMethod]) => {
      const valid =
        !(selectedMethod.disabled && selectedMethod.disabled_message) &&
        !(selectedMethod.status && selectedMethod.status.message) &&
        this.formGroup.valid;
      this.validate.emit({ valid, source: 'submit' });
    });

  resellerId$ = this._orderformFascade.rootState$?.pipe(
    map((e) => e.order.settings.config.siteowner_id),
    shareReplay(1)
  );

  private readonly SELECTED_PAYMENT_PROVIDER_ID_STORAGE_KEY = 'selectedPaymentProvider';

  constructor(
    private _orderformFascade: OrderformFascade,
    private _toaster: ToasterService,
    private readonly _element: ElementRef<HTMLElement>,
    private readonly localStorageService: LocalStorageService,
    private readonly growthBookService: GrowthBookService
  ) {
    this.selectedPaymentMethod$
      .pipe(distinctUntilChanged((a, b) => a.payment_provider_id === b.payment_provider_id))
      .pipe(withLatestFrom(this._paymentMethodList$), takeUntil(this._destroy$))
      .subscribe(([selectedPaymentProvider, availablePaymentMethods]) => {
        this._blockSendUpdate = true;
        this.formGroup.get('providerId').patchValue(selectedPaymentProvider.payment_provider_id);
        this.formGroup.get('providerData').patchValue({ valid: true });
        this.formGroup.updateValueAndValidity();
        this._blockSendUpdate = false;
        if (this._paymentProviderNotAvailable$.value.indexOf(selectedPaymentProvider.payment_provider_id) >= 0) {
          this.formGroup.get('providerId').patchValue(availablePaymentMethods[0].payment_provider_id);
        }
      });

    this.formGroup
      .get('providerId')
      .valueChanges.pipe(
        filter(() => !this._blockSendUpdate),
        takeUntil(this._destroy$),
        // complete selected payment plan by selected payment id
        withLatestFrom(this._paymentMethodList$)
      )
      .subscribe(([providerId, methodList]) => {
        const selectedPaymentProvider = methodList.find((method) => method.payment_provider_id === providerId);
        if (selectedPaymentProvider) {
          this._orderformFascade.setOrderStore({
            payment_provider: selectedPaymentProvider,
          });

          this.localStorageService.setStoreItem(
            this.SELECTED_PAYMENT_PROVIDER_ID_STORAGE_KEY,
            selectedPaymentProvider.payment_provider_id
          );
        }
      });

    this.formGroup
      .get('providerData')
      .valueChanges.pipe(
        filter(() => !this._blockSendUpdate),
        takeUntil(this._destroy$)
      )
      .subscribe((providerData) => {
        const data = { ...providerData };
        delete data['valid'];
        this._orderformFascade.setOrderStore({
          payment_provider_meta: data,
        });
      });
  }

  ngOnInit(): void {
    this.checkForCachedPaymentMethod();
  }

  private checkForCachedPaymentMethod() {
    // check for pay_provider_id or first_paymethod GET parameters and skip selection if they exist
    let params = new URL(document.location.toString()).searchParams;
    let pay_provider_id = params.get('pay_provider_id');
    let first_paymethod = params.get('first_paymethod');

    if (pay_provider_id || first_paymethod) return;

    this._paymentMethodList$.pipe(take(1), takeUntil(this._destroy$)).subscribe((methodList) => {
      let cachedPaymentProviderId = this.localStorageService.getStoreItem(
        this.SELECTED_PAYMENT_PROVIDER_ID_STORAGE_KEY
      );
      const selectedPaymentProvider = methodList.find(
        (method) => method.payment_provider_id === cachedPaymentProviderId
      );

      // check selected payment provider is available, if not, skip
      if (!selectedPaymentProvider) return;

      this._orderformFascade.setOrderStore({
        payment_provider: selectedPaymentProvider,
      });
    });
  }

  trackByRendererType(index: number, provider: OrderformPaymentProvider) {
    return provider.renderer_type;
  }

  public addProviderToInvisibleList(provider_id: string) {
    this._paymentProviderNotAvailable$.next([provider_id, ...this._paymentProviderNotAvailable$.value]);
  }

  private async _onBeforeSubmit(): Promise<OrderFormValidationResult> {
    if (this.formGroup.pending) {
      await firstValueFrom(this.formGroup.statusChanges.pipe(filter((status) => status !== 'PENDING')));
    }
    let valid = this.formGroup.valid;
    let invalidFieldList = getInvalidFieldListFromFormGroup(this.formGroup);
    const selectedMethod = await firstValueFrom(this.selectedPaymentMethod$);
    // Display message if amount is too high
    if (selectedMethod.disabled && selectedMethod.disabled_message) {
      this._toaster.error(selectedMethod.disabled_message);
      valid = false;
      invalidFieldList.push({
        id: 'generic',
        errors: ['method_disabled:' + selectedMethod.disabled_message],
      });
    }

    if (selectedMethod.status?.message) {
      valid = false;
      invalidFieldList.push({
        id: 'generic',
        errors: ['status:' + selectedMethod.status.message],
      });
    }

    return Promise.resolve({
      valid,
      component: 'ds-orderform-payment-methods',
      invalidFieldList,
    });
  }

  public onPrimerGoogleAppleTypeDetected(type: PrimerPaymentMethodType): void {
    this.detectedGoogleAppleMethodType = type;

    if (type === PrimerPaymentMethodType.NONE) {
      this.paymentMethodSelectionList$ = this.paymentMethodSelectionList$.pipe(
        map((methods) => methods.filter((method) => method.pay_method !== 'apple_google_pay'))
      );
    } else {
      this.paymentMethodSelectionList$ = this.paymentMethodSelectionList$.pipe(
        map((methods) =>
          methods.map((method) => {
            if (method.pay_method === 'apple_google_pay') {
              return {
                ...method,
                icon_data: {
                  ...method.icon_data,
                  alt: type === PrimerPaymentMethodType.GOOGLE_PAY ? 'Google Pay' : 'Apple Pay',
                },
              };
            }
            return method;
          })
        )
      );
    }

    // After filtering, if this was the selected method and it's now removed,
    // select the first available method instead
    this.paymentMethodSelectionList$
      .pipe(take(1), withLatestFrom(this.selectedPaymentMethod$))
      .subscribe(([availableMethods, selectedMethod]) => {
        if (
          type === PrimerPaymentMethodType.NONE &&
          selectedMethod.pay_method === 'apple_google_pay' &&
          availableMethods.length > 0
        ) {
          this._orderformFascade.setOrderStore({
            payment_provider: availableMethods[0],
          });
        }
      });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._subscriptionBeforeSubmit.unsubscribe();
    this._formValidationSubscription.unsubscribe();
    this._submitFailedSubscription.unsubscribe();
  }
}
